import {
  ASSESSMENT_INTRO,
  BAND_SPECTRUM,
  dataByAssessmentBand,
} from "@/constants/terminal/assessment-band"
import {
  dataByDetectionClass,
  DetectionClass,
} from "@/constants/terminal/detection-class"
import {
  dataByPortfolioCompanyStatus,
  PortfolioCompanyStatus,
} from "@/constants/terminal/portfolio-company-status"
import { detectionTypeLabel } from "@/lib/terminal/detection-type-label"
import {
  metricDefinitions,
  sectionDefinitions,
} from "@/lib/terminal/file-label-definitions"
import { NIGHTSHIFT_ORIGIN } from "@/lib/terminal/nightshift-domain"

// Night Shift's Definitions page (D48), which also serves as the data
// dictionary for Export Files. It follows the company page: one box per File
// box, in the page's order, then Spark, Smoke and Fire as boxes of their own
// (Dhanur, 25 Oct 2026). A term earns a place only if the company page shows
// its meaning on hover or the export writes it, and every Section and Item
// the export writes is defined here under the same name. Where the company
// page has a hover, the meaning is that same text (file-label-definitions.ts).

type DefinitionTerm = {
  term: string
  meaning: string
  format: string
  example: string
}

type DefinitionSection = {
  section: string
  meaning: string
  terms: DefinitionTerm[]
}

// The words a title keeps in lower case unless they lead it.
const minorWords = new Set(["of", "and", "or", "at", "in", "on", "the", "a"])

// The Item a Detection is exported under: its Detection Label ("PREDATORY
// LIEN") in title case ("Predatory Lien"). Types that share a Label share the
// Item, and its class's Section tells a Spark from a Smoke.
export const exportDetectionItem = (detection: {
  typeKey: string
  typeLabel: string
}) =>
  detectionTypeLabel(detection)
    .toLowerCase()
    .split(" ")
    .map((word, index) =>
      index > 0 && minorWords.has(word)
        ? word
        : word.replace(
            /(^|-)([a-z])/g,
            (_, start, letter) => `${start}${letter.toUpperCase()}`
          )
    )
    .join(" ")

// What each Detection Label means, within its class (COMPANY
// terminal/_scratch/detection-label-urgent-release-2026-10-27.md, 27 Oct
// 2026). A meaning explains what the pipeline selects today; it never
// redefines it. A Label two types share has one meaning covering both.
export const detectionMeaningsByClass: Record<
  DetectionClass,
  Record<string, string>
> = {
  [DetectionClass.SPARK]: {
    Deception:
      "A customer reports being misled about something that mattered, such as price, savings or what was promised.",
    "Sales Misconduct":
      "A customer reports harmful sales conduct, such as pressure, deception, dismissive treatment or broken sales promises.",
    "Faulty Work":
      "A customer reports problems with the quality, safety or code compliance of the installation.",
    "Poor Aftercare":
      "A customer reports poor responsiveness or help after installation, including service and warranty needs.",
    "Badly Run Jobs":
      "A customer reports failures in scheduling, permitting, crew coordination, timelines or communication from contract through activation.",
    "Poor Value":
      "A customer reports that the cost, savings or delivered outcome did not match what they paid or were promised.",
    Abandonment:
      "A customer reports that the company abandoned their system, leaving an unfinished or nonfunctional installation without meaningful help.",
    "Predatory Lien":
      "A customer reports a lien based on a forged or unauthorized signature, or an installer's lien arising from a dispute over the work.",
    "Unpaid Sub Lien":
      "A customer reports a lien filed by a subcontractor or supplier because the installer did not pay them.",
    "Ghost Lien":
      "A customer reports a financing lien left on their home after a cancelled or unfinished job because the installer did not unwind the financing.",
  },
  [DetectionClass.SMOKE]: {
    Deception:
      "A recurring share of customer reports describe being misled, including misleading quotes, broken promises or bait-and-switch. These are customer accounts, not a ruling on the allegations.",
    "Sales Misconduct":
      "Customers' satisfaction with how salespeople treated them stays low or falls sharply over consecutive quarters. Reports can concern pressure, deception, dismissive treatment or broken sales promises.",
    "Faulty Work":
      "Customers' satisfaction with the quality of their installation stays low or falls sharply over consecutive quarters.",
    Undependable:
      "Customers' satisfaction with support after installation stays low or falls sharply, or customers who have lived with their system for a year or more report low satisfaction. The Detection says which applies.",
    "Badly Run Jobs":
      "Customers' satisfaction with scheduling, permitting, coordination or communication from contract through activation stays low or falls sharply over consecutive quarters.",
    "Poor Value":
      "Customers' satisfaction with what they received for the price stays low or falls sharply over consecutive quarters.",
    "Activity Decline":
      "Reviews and complaints stopped after a steady flow, or fell sharply alongside signs that the company went dark, such as a closed Google profile or a parked website. A decline alone does not establish that the company closed.",
    "Reputation Risk":
      "A complaint body or review site shows a warning, such as high or rising complaint counts, unresolved complaints, an alert, a low rating or a closed profile. Sources include the BBB and others. The Detection says which warning applies.",
    "Suspect Reviews":
      "An unusually concentrated burst of high-rated reviews suggests solicited or coordinated reviews. Night Shift leaves those reviews out of its rating and review figures. The pattern does not prove every review in the burst is false.",
    Abandonment:
      "Customers report abandoned systems or liens left on their homes. This Label also covers patterns of liens alone, where no abandoned system is reported.",
    "Debt Stacking":
      "Several short-term business lenders, such as cash-advance funders, factors or equipment lessors, have filed secured claims on the company's assets. Ordinary bank and supplier filings don't count.",
    "Lawsuit Pending":
      "Court records show a lawsuit against the company, in federal or state court, that is open or has no recorded ending. Suits by individuals may be counted together; the plaintiffs are not necessarily customers.",
    "Entity Recycling":
      "Another legal entity uses the trade name and a shared address or phone of an entity the company dissolved, withdrew or forfeited.",
    "Entity Lapsed":
      "The company's legal entity is dissolved, forfeited or otherwise not in good standing while other evidence shows the company still operates.",
    "Injury Pattern":
      "More than one official severe-injury report names the company as the employer. A report is not a safety citation. Workplace-safety research is not currently active.",
    "Credential Risk":
      "A licensing record shows a deceased qualifier, a null-and-void license or no qualified individual, and the company holds no other active license of that class from the same board.",
    "License at Risk":
      "A licensing board matter has reached formal charges or proceedings, such as an administrative complaint, a pending citation or a qualifying hearing. The outcome is undecided; loss of the license is not certain.",
    "Board Complaint":
      "A complaint or directive remains open with a licensing board, short of formal charges. Coverage today is Arizona's Registrar of Contractors. A complaint does not prove its allegations.",
    "Coverage Lapse":
      "Oregon's licensing board records a past suspension for a lapse in proof of insurance or bond, which the board does not treat as discipline. Missing proof does not by itself establish that there was no coverage.",
  },
  [DetectionClass.FIRE]: {
    "License Action":
      "A licensing board suspended, revoked or restricted the company's license, or entered a final citation or disciplinary order against it. Read from the board's own record.",
    "Qualifier Action":
      "A licensing board restricted or disciplined the license of the person who qualifies the company to do the work. The company holds another active license of the same class from that board.",
    "Bond Suspension":
      "A licensing board suspended or restricted a license only because its required bond was cancelled, lapsed or insufficient: a financial lapse, not discipline for conduct.",
    "License Warning":
      "California's licensing board issued a Letter of Admonishment, a formal warning the company may contest.",
    "Out of Business":
      "The company has stopped operating: it said so itself, or its legal entity was dissolved and its required licenses are gone, with no remaining operating footprint.",
    Bankruptcy:
      "A court record names the company as the debtor in a bankruptcy proceeding. The Detection states the chapter and the case status where known.",
    "Agency Action":
      "A government authority, such as the CFPB, FTC or a state attorney general, brought an enforcement action against the company that has no final outcome yet, including some interim or emergency orders. It does not establish that every allegation was proved.",
    "Agency Order":
      "A government enforcement order, judgment or binding settlement applies to the company. It does not necessarily include an admission or a ruling on every allegation.",
    "Lawsuit Lost":
      "A court entered a civil judgment against the company. A lien recorded to collect a judgment shows separately as Judgment Lien.",
    "Tax Lien":
      "A tax authority recorded a lien against the company for unpaid taxes. A released or lapsed lien does not count.",
    "Judgment Lien":
      "A creditor recorded a lien against the company's property to collect a court judgment. A released or lapsed lien does not count.",
    Collections:
      "Several different creditors pursued the company within a year through lawsuits, tax liens or judgment liens. Each matter is also its own Detection.",
    "Safety Violation":
      "A federal OSHA inspection cited the company for a safety violation. An injury report alone is never a citation. Workplace-safety research is not currently active.",
  },
}

// Each Detection class as the Detections pane names it, which is also the
// Section the export writes for its Detections, Spark first as the pane
// reads.
export const DETECTION_CLASS_ORDER = [
  DetectionClass.SPARK,
  DetectionClass.SMOKE,
  DetectionClass.FIRE,
]

const detectionClassMeaning: Record<DetectionClass, string> = {
  [DetectionClass.SPARK]:
    "The most notable reports of customer harm. Each Detection describes one customer's account.",
  [DetectionClass.SMOKE]:
    "Warning patterns, unresolved matters and adverse conditions. Some conditions are established in official records.",
  [DetectionClass.FIRE]:
    "Confirmed adverse events. The action, filing or event is established; the allegations behind it are not necessarily proved.",
}

const percentTerm = (term: string): DefinitionTerm => ({
  term,
  meaning: metricDefinitions[term],
  format: "Percentage",
  example: "71%",
})

const tradeTerm = (term: string): DefinitionTerm => ({
  term,
  meaning: metricDefinitions[term],
  format: "Share and satisfaction",
  example: "15% share · 90% sat",
})

const plainTerm = (
  term: string,
  meaning: string,
  example: string,
  format = "Text"
): DefinitionTerm => ({ term, meaning, format, example })

export const definitionSections: DefinitionSection[] = [
  {
    section: "Overview",
    meaning:
      "The company's Service, its Score and Assessment, and the Brief explaining what is happening and why it matters.",
    terms: [
      plainTerm(
        "Link",
        "The company's page in Night Shift.",
        `${NIGHTSHIFT_ORIGIN}/company-name?portfolio=your-portfolio`,
        "Link"
      ),
      plainTerm(
        "Service",
        `${dataByPortfolioCompanyStatus[PortfolioCompanyStatus.PORTFOLIO].label}: Night Shift keeps checking the company and updates its Score as records change. ${dataByPortfolioCompanyStatus[PortfolioCompanyStatus.PIPELINE].label}: a single assessment that is not updated.`,
        dataByPortfolioCompanyStatus[PortfolioCompanyStatus.PORTFOLIO].label,
        `${dataByPortfolioCompanyStatus[PortfolioCompanyStatus.PORTFOLIO].label} or ${dataByPortfolioCompanyStatus[PortfolioCompanyStatus.PIPELINE].label}`
      ),
      plainTerm(
        "Score",
        "Night Shift's rating of the company's counterparty risk, a whole number from 0 to 100. Higher means lower risk. The Score sets the Assessment; an Unclear company has no Score.",
        "58",
        "Whole number, 0–100"
      ),
      plainTerm(
        "Assessment",
        `${ASSESSMENT_INTRO} The Score sets which applies; a company with no Score is Unclear.`,
        "Stressed",
        "One of the bands below"
      ),
      ...BAND_SPECTRUM.map((band) =>
        plainTerm(
          dataByAssessmentBand[band].label,
          `${dataByAssessmentBand[band].definition} ${
            dataByAssessmentBand[band].scoreRange
              ? `Score ${dataByAssessmentBand[band].scoreRange}.`
              : "No Score."
          }`,
          dataByAssessmentBand[band].label,
          "Assessment"
        )
      ),
      plainTerm(
        "Brief",
        "Night Shift's explanation of what is happening at the company and why it matters.",
        "The company's customers report…"
      ),
      plainTerm(
        "Updated",
        "When the company's current Score and Assessment were published.",
        "2026-10-08",
        "Date, year-month-day"
      ),
    ],
  },
  {
    section: "Reviews",
    meaning: sectionDefinitions.Reviews,
    terms: [
      plainTerm(
        "Verified customers",
        "Reviews written by people who actually hired the company, not shoppers, across the review platforms Night Shift monitors. Shown relative to the Portfolio, as a proxy for the size of a company's customer base.",
        "16% of the Portfolio's verified customers",
        "Share of the Portfolio"
      ),
      {
        term: "Aggregate Rating",
        meaning: metricDefinitions["Aggregate Rating"],
        format: "Stars",
        example: "4.0 stars",
      },
      {
        term: "Total Reviews",
        meaning: metricDefinitions["Total Reviews"],
        format: "Count",
        example: "376 reviews",
      },
      {
        term: "12-Mo Review Avg",
        meaning: metricDefinitions["12-Mo Review Avg"],
        format: "Reviews per month",
        example: "15.8 reviews/mo",
      },
      {
        term: "vs Prior 12-Mo",
        meaning: metricDefinitions["vs Prior 12-Mo"],
        format: "Signed percentage",
        example: "+12%",
      },
      plainTerm(
        "Platforms monitored",
        "The review sites Night Shift tracks for the company. Each site has its own row, named for the site, such as Google or BBB, with its rating, its review count and a link to the profile.",
        "4.4 stars · 119 reviews",
        "Stars and count"
      ),
    ],
  },
  {
    section: "Character",
    meaning: sectionDefinitions.Character,
    terms: [
      "Buyer Rate",
      "Buyer Satisfaction",
      "Long-Term Review Rate",
      "Long-Term Satisfaction",
      "Deception Rate",
    ].map(percentTerm),
  },
  {
    section: "Execution",
    meaning: sectionDefinitions.Execution,
    terms: [
      "Sales Conduct",
      "Workmanship",
      "Value",
      "Customer Support",
      "Project Management",
    ].map(percentTerm),
  },
  {
    section: "Trade Mix",
    meaning: sectionDefinitions["Trade Mix"],
    terms: [
      ...["Roofing", "Storage", "Electrical", "HVAC"].map(tradeTerm),
      plainTerm(
        "Services Marketed",
        metricDefinitions["Services Marketed"],
        "Battery storage, EV charging, Solar",
        "Comma-separated list"
      ),
    ],
  },
  {
    section: "Background",
    meaning: sectionDefinitions.Background,
    terms: [
      plainTerm(
        "Legal Name",
        "The company's registered legal name, when it differs from the name shown.",
        "Example Solar LLC"
      ),
      plainTerm(
        "Also Known As",
        "A brand name the company trades under, when it differs from the name shown.",
        "Example Solar"
      ),
      plainTerm("Website", "The company's own website.", "examplesolar.com"),
      plainTerm(
        "Founded",
        "The year the company was established.",
        "2021",
        "Year"
      ),
      plainTerm(
        "Type",
        "The kind of company, such as a solar installer, an electrical contractor or a distributor.",
        "Distributor"
      ),
      plainTerm(
        "Status",
        "Whether the company is operating, as the records state it.",
        "Active"
      ),
      plainTerm(
        "Franchise",
        "Whether the company operates as a franchise.",
        "No",
        "Yes or No"
      ),
      plainTerm(
        "HQ",
        "The city and state of the company's headquarters.",
        "Austin, TX"
      ),
      plainTerm(
        "Service Area",
        "The states the company serves.",
        "AZ, CA, NV",
        "State codes"
      ),
      plainTerm(
        "Licensed In",
        "Every state where the company holds a contractor license on record. Each license then has its own row, named for its state, with its type and number; its issuing board, status and expiry are in Detail.",
        "AZ, CA",
        "State codes"
      ),
    ],
  },
  // Spark, Smoke and Fire are boxes of their own, as the export's Sections
  // name them (Dhanur, 25 Oct 2026).
  ...DETECTION_CLASS_ORDER.map((detectionClass) => ({
    section: dataByDetectionClass[detectionClass].label,
    meaning: detectionClassMeaning[detectionClass],
    terms: Object.entries(detectionMeaningsByClass[detectionClass]).map(
      ([item, meaning]) => plainTerm(item, meaning, item, "Detection")
    ),
  })),
]
